import React from 'react';
import { useListContext } from 'react-admin';
import { cn } from '../../lib/utils';

/**
 * List-screen scaffold — DESIGN_SYSTEM.md §6.
 * Layout: stat row → toolbar (tabs | filters+sort) → token row (tokens | count) → table.
 * Slots are React nodes so each section wires its own RA-aware controls
 * (filters/sort/tokens land in Phase 3, per-section configs in Phase 4).
 */
export type ListScreenProps = {
  /** Stat cards row. */
  stats?: React.ReactNode;
  /** Segmented status tabs (toolbar left). */
  tabs?: React.ReactNode;
  /** Filter + sort controls (toolbar right). */
  actions?: React.ReactNode;
  /** Active filter/sort tokens (token row left). */
  tokens?: React.ReactNode;
  /** Result count (token row right); defaults to <ResultCount/>. */
  count?: React.ReactNode;
  /** The DataTable. */
  children: React.ReactNode;
  className?: string;
};

export function ListScreen({
  stats,
  tabs,
  actions,
  tokens,
  count,
  children,
  className,
}: ListScreenProps) {
  const showToolbar = Boolean(tabs || actions);
  const showTokenRow = Boolean(tokens || count !== null);

  return (
    <div className={cn('space-y-5 p-6', className)}>
      {stats}

      {showToolbar && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">{tabs}</div>
          <div className="flex items-center gap-2">{actions}</div>
        </div>
      )}

      {showTokenRow && (
        <div className="flex min-h-6 flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">{tokens}</div>
          <div className="text-[13px] text-ink-2">{count ?? <ResultCount />}</div>
        </div>
      )}

      {children}
    </div>
  );
}

/** "N results" pulled from the RA list context. */
export function ResultCount() {
  const { total, isLoading } = useListContext();
  if (isLoading && total === undefined) return null;
  return (
    <span>
      <span className="font-semibold text-ink">{total ?? 0}</span> result{total === 1 ? '' : 's'}
    </span>
  );
}
